import React, { useCallback, useMemo, useState } from 'react';
import { Box, Button, Divider, Grid, Paper, Typography } from '@material-ui/core';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { useDispatch } from 'react-redux';
import { IdPropType } from '../../../proptypes/basic';
import { useEmail, retryEmail } from '../emailsSlice';
import { formatDate } from '../../date/utils';
import MailContacts from './MailContacts';
import MailAttachments from './MailAttachments';
import EmailStatusIndicator from './EmailStatusIndicator';
import { useSnackbar } from '../../snackbar/hooks';
import { handleMailIframeLoad } from '../utils';

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(2),
        margin: '20px auto',
        background: '#fff',
        maxWidth: '900px',
    },

    mailMeta: {
        marginBottom: theme.spacing(2),
    },

    mailText: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(3),
        whiteSpace: 'pre-line',
    },

    empty: {
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
}));
const MailContent = ({ mailId }) => {
    const classes = useStyles();
    const [mail] = useEmail(mailId);
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const [retrying, setRetrying] = useState(false);

    const handleRetry = useCallback(async () => {
        setRetrying(true);
        try {
            await dispatch(retryEmail({ id: mailId }));
            enqueueSnackbar('Der erneute Versand wurde gestartet.', { variant: 'success' });
        } catch (error) {
            enqueueSnackbar('Erneuter Versand konnte nicht gestartet werden.', {
                variant: 'error',
            });
        } finally {
            setRetrying(false);
        }
    }, [dispatch, mailId, enqueueSnackbar]);

    const iframe = document.createElement('iframe');
    const isHtmlEmailSupported = 'sandbox' in iframe && 'srcdoc' in iframe;

    const htmlContent = useMemo(() => {
        const whiteSpace = /<\/?[a-z][\s\S]*>/i.test(mail?.body || '') ? 'normal' : 'pre-wrap';

        return `
    <html>
      <head>
        <meta http-equiv="Content-Security-Policy" content="script-src 'none'">
        <base target="_blank">
        <style>
          body {
            margin: 0;
            overflow-y: hidden;
            color: #212529;
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
            font-size: 14px;
            font-weight: 400;
            line-height: 1.5;
            white-space: ${whiteSpace};
          }
          html:not(.x), body:not(.x) {
            height: auto !important;
          }
          p:first-child { margin-top: 0; }
          p:last-child { margin-bottom: 0; }
          a[href] { color: #3781B8; text-decoration: none; }
          a[href]:hover { text-decoration: underline; }
          blockquote[type=cite] {
            margin: 0 0 0 .8ex;
            border-left: 1px solid #ccc;
            padding-left: 1ex;
          }
          img { max-width: 100%; }
          ul, ol { padding: 0; margin: 0 0 10px 25px; }
          ul { list-style-type: disc; }
        </style>
      </head>
      <body>${mail?.body}</body>
    </html>
  `;
    }, [mail]);

    return (
        <Paper elevation={1} className={classes.root}>
            {mail ? (
                <>
                    <Grid className={classes.mailMeta}>
                        <Grid container justify="space-between">
                            <Grid item>
                                <MailContacts mailId={mailId} />
                            </Grid>
                            <Grid item>
                                <Typography color="textSecondary" gutterBottom>
                                    {formatDate(mail.received_at, 'DD.MM.YYYY HH:mm')}
                                </Typography>
                            </Grid>
                        </Grid>
                        <Grid container spacing={1} alignItems="center">
                            <Grid item>
                                <Typography variant="h4">{mail.subject}</Typography>
                            </Grid>
                            {!!mail.status && (
                                <Grid item>
                                    <EmailStatusIndicator
                                        status={mail.status}
                                        failedReason={mail.failed_reason}
                                        outbound={!!mail.sentAt}
                                        fontSize="default"
                                    />
                                </Grid>
                            )}
                            {mail.status === 'failed' && (
                                <Grid item>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        color="primary"
                                        disabled={retrying}
                                        onClick={handleRetry}
                                    >
                                        Erneut senden
                                    </Button>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                    <Divider />
                    <MailAttachments mailId={mailId} />
                    <Divider />
                    {isHtmlEmailSupported ? (
                        <Box sx={{ width: '100%' }}>
                            <iframe
                                title="mail content"
                                style={{ width: '100%', border: 0, marginTop: '5px' }}
                                onLoad={handleMailIframeLoad}
                                srcDoc={htmlContent}
                                sandbox="allow-popups allow-popups-to-escape-sandbox allow-same-origin"
                                csp="script-src 'none'"
                                className="h-full w-full"
                            />
                        </Box>
                    ) : (
                        <div>Ihr Browser wird nicht unterstützt</div>
                    )}
                    {/*
	                    <Typography
	                        className={classes.mailText}
	                        variant="body1"
	                        dangerouslySetInnerHTML={{ __html: mail.body }}
	                    />
                    */}
                </>
            ) : (
                <Typography
                    variant="h3"
                    component="h3"
                    className={classes.empty}
                    color="textSecondary"
                >
                    Keine E-Mail ausgewählt.
                </Typography>
            )}
        </Paper>
    );
};

MailContent.propTypes = {
    mailId: IdPropType,
};

MailContent.defaultProps = {
    mailId: null,
};

export default MailContent;
